import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ReactNode } from 'react';
import { FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Progress } from '@sorare/core/src/atoms/loader/Progress';
import { LabelS } from '@sorare/core/src/atoms/typography';
import { Avatar } from '@sorare/core/src/components/user/Avatar';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { isType } from '@sorare/core/src/lib/gql';

import type {
  DuoTaskLeg_user,
  ThresholdsDuoTaskScores_taskInterface,
} from './__generated__/index.graphql';

type Props = {
  task: ThresholdsDuoTaskScores_taskInterface;
};

const Root = styled(Horizontal)`
  padding: var(--unit) var(--intermediate-unit);
  box-shadow:
    0 0 8px 4px rgba(23, 158, 255, 0.2) inset,
    0 4px 12px 0 rgba(5, 28, 86, 0.7);
  border: 1px solid rgba(var(--c-rgb-superRare), 1);
  border-radius: var(--unit);
`;

const Img = styled.img`
  width: var(--quadruple-unit);
  height: var(--quadruple-unit);
  object-fit: contain;
`;

const Leg = styled(Vertical)`
  min-width: 64px;
`;

const DuoTaskLeg = ({
  score,
  target,
  avatar,
}: {
  score: number;
  target: number;
  avatar: ReactNode;
}) => {
  return (
    <Leg>
      <Horizontal gap={0.5}>
        {avatar}
        <LabelS bold>
          <FormattedNumber value={score} maximumFractionDigits={2} />
        </LabelS>
      </Horizontal>
      <Progress
        value={score}
        max={target}
        color={
          score > target
            ? 'var(--c-score-high)'
            : 'linear-gradient(90deg, #0B85DD 76.47%, #179EFF 88.3%, #63C3FF 100%)'
        }
      />
    </Leg>
  );
};

DuoTaskLeg.fragments = {
  user: gql`
    fragment DuoTaskLeg_user on User {
      slug
      ...Avatar_user
    }
    ${Avatar.fragments.user}
  ` as TypedDocumentNode<DuoTaskLeg_user>,
};

export const ThresholdsDuoTaskScores = ({ task }: Props) => {
  const { currentUser } = useCurrentUserContext();
  const isLaptopAndAbove = useIsLaptop();
  if (!isType(task, 'ThresholdsDuoConnectionTask') || !currentUser) {
    return null;
  }

  const { target, templateIconUrl, connectedLineup, connectedUser } = task;

  const partnerSo5Ranking = connectedLineup?.so5Rankings[0];
  const mySo5Ranking = task.myLineup?.so5Rankings[0];

  return (
    <Root>
      {!!isLaptopAndAbove && <Img src={templateIconUrl} alt="" />}
      <DuoTaskLeg
        score={mySo5Ranking?.score || 0}
        target={target}
        avatar={<Avatar user={currentUser} rounded size={2} />}
      />
      <DuoTaskLeg
        score={partnerSo5Ranking?.score || 0}
        target={target}
        avatar={<Avatar user={connectedUser} rounded size={2} />}
      />
    </Root>
  );
};

ThresholdsDuoTaskScores.fragments = {
  taskInterface: gql`
    fragment ThresholdsDuoTaskScores_taskInterface on TaskInterface {
      id
      target
      title
      iconUrl
      templateIconUrl
      ... on ThresholdsDuoConnectionTask {
        id
        myLineup {
          id
          so5Rankings {
            id
            score
          }
        }
        connectedUser {
          slug
          ...DuoTaskLeg_user
        }
        connectedLineup {
          id
          so5Rankings {
            id
            score
          }
        }
      }
    }
    ${DuoTaskLeg.fragments.user}
  ` as TypedDocumentNode<ThresholdsDuoTaskScores_taskInterface>,
};
